/**
 * Cache of discovered tree leaves. Leaves are public on-chain data, so the
 * cache is unencrypted; correctness never depends on it because every use
 * re-verifies the rebuilt root against the tree account (see leafDiscovery).
 */

export interface SolanaLeafCacheEntry {
  schemaVersion: 1;
  leaves: string[];
  /** Newest transaction folded into `leaves`; the next scan resumes after it. */
  newestSignature: string | null;
  /** Slot of that transaction. */
  newestSlot: number;
  root: string;
}

export interface SolanaLeafCache {
  load(key: string): Promise<SolanaLeafCacheEntry | null>;
  save(key: string, entry: SolanaLeafCacheEntry): Promise<void>;
  clear(key: string): Promise<void>;
}

export function solanaLeafCacheKey(
  networkKey: string,
  programId: string,
  treeIndex: number,
): string {
  return `${networkKey}:${programId}:${treeIndex}`;
}

function isEntry(value: unknown): value is SolanaLeafCacheEntry {
  const entry = value as SolanaLeafCacheEntry | null;
  return Boolean(
    entry &&
    typeof entry === "object" &&
    entry.schemaVersion === 1 &&
    Array.isArray(entry.leaves) &&
    entry.leaves.every((leaf) => /^0x[0-9a-f]{64}$/.test(leaf)) &&
    (entry.newestSignature === null ||
      typeof entry.newestSignature === "string") &&
    Number.isSafeInteger(entry.newestSlot) &&
    /^0x[0-9a-f]{64}$/.test(entry.root),
  );
}

export function createMemorySolanaLeafCache(): SolanaLeafCache {
  const entries = new Map<string, SolanaLeafCacheEntry>();
  return {
    async load(key) {
      const entry = entries.get(key);
      return entry ? { ...entry, leaves: [...entry.leaves] } : null;
    },
    async save(key, entry) {
      entries.set(key, { ...entry, leaves: [...entry.leaves] });
    },
    async clear(key) {
      entries.delete(key);
    },
  };
}

const DB_NAME = "stealthos-solana-joinsplit-v2";
const STORE = "leaves";

function openDatabase(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE)) {
          request.result.createObjectStore(STORE);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function transact<T>(
  database: IDBDatabase,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  return new Promise((resolve) => {
    try {
      const transaction = database.transaction(STORE, mode);
      const request = run(transaction.objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      transaction.onabort = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/**
 * IndexedDB-backed cache that degrades to memory when storage is missing or
 * failing; a broken cache costs a full rescan, never a wrong tree.
 */
export function createIndexedDbSolanaLeafCache(): SolanaLeafCache {
  const memory = createMemorySolanaLeafCache();
  let databasePromise: Promise<IDBDatabase | null> | null = null;
  const database = () => {
    if (!databasePromise) databasePromise = openDatabase();
    return databasePromise;
  };
  return {
    async load(key) {
      const db = await database();
      if (!db) return memory.load(key);
      const value = await transact(db, "readonly", (store) => store.get(key));
      return isEntry(value) ? value : null;
    },
    async save(key, entry) {
      const db = await database();
      if (!db) return memory.save(key, entry);
      await transact(db, "readwrite", (store) => store.put(entry, key));
    },
    async clear(key) {
      const db = await database();
      if (!db) return memory.clear(key);
      await transact(db, "readwrite", (store) => store.delete(key));
    },
  };
}
